import { FC } from 'react';
import { Box, Stack, Theme, Typography, Link, useTheme } from '@mui/material';
import { useBrandVersion } from '../../hooks';
import { getBrandInfo } from '../../utils';

export const newBrandBottomStripMinHeight = (theme: Theme): string =>
    `calc(${theme.spacing(4)} + ${theme.typography.body1.lineHeight})`;

const NEW_BRAND_TERMS_OF_SERVICE_URL =
    'https://archive.transcepta.com/resources/HighRadius_Transcepta_Terms_of_Service.pdf';
const NEW_BRAND_PRIVACY_POLICY_URL =
    'https://archive.transcepta.com/resources/HighRadius_Transcepta_Privacy_Policy.pdf';
const NEW_BRAND_RESTRICTIONS_URL =
    'https://archive.transcepta.com/resources/HighRadius_Transcepta_Instructions_and_Restrictions.pdf';

const LEGACY_PRIVACY_POLICY_URL = 'https://archive.transcepta.com/resources/Transcepta Privacy Policy.pdf';
const LEGACY_RESTRICTIONS_URL =
    'https://archive.transcepta.com/resources/Transcepta%20Instructions%20and%20Restrictions.pdf';

interface IFooterProps {
    logout?: Function;
    termsOfServiceURL: string;
    compact?: boolean;
}

export const Footer: FC<IFooterProps> = ({ logout, termsOfServiceURL, compact = false }) => {
    const theme = useTheme();
    const brandVersion = useBrandVersion();
    const { brandName, formalBrandName } = getBrandInfo(brandVersion);
    const isNewBrand = brandVersion === 'newBrand';

    const tosURL = isNewBrand ? NEW_BRAND_TERMS_OF_SERVICE_URL : termsOfServiceURL;
    const privacyURL = isNewBrand ? NEW_BRAND_PRIVACY_POLICY_URL : LEGACY_PRIVACY_POLICY_URL;
    const restrictionsURL = isNewBrand ? NEW_BRAND_RESTRICTIONS_URL : LEGACY_RESTRICTIONS_URL;

    const linkColorSx = isNewBrand
        ? { color: 'primary.main', '&:hover': { color: 'primary.dark' } }
        : { color: 'common.white', textDecoration: 'underline' };

    return (
        <Box
            data-testid="portal-footer"
            sx={[
                {
                    width: '100%',
                    p: compact ? 1 : 2,
                    flexShrink: 0,
                    '& .MuiTypography-root, & a': { fontSize: 12 },
                },
                isNewBrand
                    ? {
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          minHeight: compact ? undefined : newBrandBottomStripMinHeight(theme),
                          boxSizing: 'border-box',
                          flexShrink: 0,
                          borderTop: 1,
                          borderColor: 'divider',
                      }
                    : { bgcolor: 'primary.700', color: 'common.white' },
            ]}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent={isNewBrand ? { md: 'space-between', lg: 'center' } : 'space-between'}
                alignItems="center"
                spacing={{ xs: 2, md: 4 }}
                useFlexGap
                flexWrap="wrap"
                sx={isNewBrand ? { textAlign: { lg: 'center' } } : undefined}
            >
                <Typography sx={{ width: { xs: '100%', md: 'auto' } }}>
                    &copy; 2006-{new Date().getFullYear()} {formalBrandName}. If you are a supplier or vendor,{' '}
                    {brandName}’s service is governed by {brandName}’s{' '}
                    <Link sx={linkColorSx} href={tosURL} target="_blank">
                        Terms of Service.
                    </Link>
                </Typography>
                <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                    <Link
                        sx={linkColorSx}
                        noWrap
                        href="mailto:support@transcepta.com?subject=Transcepta Manager Feedback"
                        target="_blank"
                    >
                        Send Feedback
                    </Link>
                    <Link sx={linkColorSx} noWrap href="mailto:support@transcepta.com" target="_blank">
                        Support
                    </Link>
                    <Link sx={linkColorSx} noWrap href={privacyURL} target="_blank">
                        Privacy Policy
                    </Link>
                    <Link sx={linkColorSx} noWrap href={restrictionsURL} target="_blank">
                        Restrictions
                    </Link>
                    {!isNewBrand && logout && (
                        <Link sx={linkColorSx} noWrap href="#" onClick={() => logout()}>
                            Logout
                        </Link>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};
